import { customElement, property } from 'lit/decorators.js';
import { ExtBaseElement } from '../../core/ExtBaseElement';
import './ext-spinner.css';

export type ExtSpinnerSize = 'sm' | 'md' | 'lg';

/**
 * <ext-spinner size="md" label="Cargando...">
 *
 * Indicador de carga CSS puro (border + animation). El color del aro usa
 * --ui-primary. Accesibilidad: role="status" + aria-label.
 *
 * Light DOM: no hay <slot>. Si hay label se renderiza como texto adjunto via
 * imperative DOM en connectedCallback/updated; si no, solo el spinner.
 *
 * Props:
 *  - size:  sm | md | lg (16 / 24 / 40 px).
 *  - label: string opcional; se muestra debajo y se usa como aria-label.
 */
@customElement('ext-spinner')
export class ExtSpinner extends ExtBaseElement {
  @property({ type: String, reflect: true }) size: ExtSpinnerSize = 'md';
  @property({ type: String }) label = '';

  protected createRenderRoot(): HTMLElement {
    return this;
  }

  render(): null {
    return null;
  }

  connectedCallback(): void {
    super.connectedCallback();
    this.setAttribute('role', 'status');
    this._buildDOM();
  }

  protected updated(changed: Map<string, unknown>): void {
    if (changed.has('label')) {
      this._syncLabel();
    }
  }

  private _buildDOM(): void {
    if (this.querySelector(':scope > .ext-spinner__ring')) {
      this._syncLabel();
      return;
    }
    // Reset + crear estructura.
    this.innerHTML = '';
    const ring = document.createElement('span');
    ring.className = 'ext-spinner__ring';
    ring.setAttribute('aria-hidden', 'true');
    this.appendChild(ring);
    this._syncLabel();
  }

  private _syncLabel(): void {
    if (this.label) {
      this.setAttribute('aria-label', this.label);
    } else {
      this.removeAttribute('aria-label');
    }
    const existing = this.querySelector(':scope > .ext-spinner__label');
    if (this.label) {
      if (existing) {
        existing.textContent = this.label;
      } else {
        const lab = document.createElement('span');
        lab.className = 'ext-spinner__label';
        lab.textContent = this.label;
        this.appendChild(lab);
      }
    } else if (existing) {
      existing.remove();
    }
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'ext-spinner': ExtSpinner;
  }
}
